import React, { useState, useEffect } from "react";
import { Row, Col, Spin, Select } from "antd";
import { useSelector, useDispatch } from "react-redux";
import Chart from "react-apexcharts";
import {
  Users,
  UserPlus,
  Eye,
  Globe,
  TrendingUp,
  Activity,
  PieChart,
  BarChart3,
  Monitor,
  Smartphone,
  Tablet,
  Zap,
  MessageSquare,
  FileText,
  ThumbsUp,
  ThumbsDown,
  Flame,
  UserCheck,
  Mail,
  Share2,
} from "lucide-react";
import {
  getSuperAdminAnalyticsAction,
  getUsageAnalyticsAction,
  getRetentionAnalyticsAction,
} from "@redux/actions/superAdminActions";
import "@assets/css/SuperAdminDashboard.css";

const { Option } = Select;

const SuperAdminDashboard = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { analytics, usageAnalytics, retentionAnalytics } = useSelector(
    (state) => state.superAdmin
  );
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [timeRange, setTimeRange] = useState("7d");

  useEffect(() => {
    handleFetchAnalytics();
  }, [timeRange, activeTab]);

  const handleFetchAnalytics = () => {
    setLoading(true);
    if (activeTab === "overview" || activeTab === "engagement") {
      dispatch(getSuperAdminAnalyticsAction(setLoading, timeRange));
    }
    if (activeTab === "usage") {
      dispatch(getUsageAnalyticsAction(setLoading, timeRange));
    }
    if (activeTab === "retention") {
      dispatch(getRetentionAnalyticsAction(setLoading, timeRange));
    }
  };

  // Stat Card Component
  const StatCard = ({ icon, iconColor, title, value, trend, trendUp, period }) => (
    <div className="stats-card">
      <div className="stats-card__content">
        <div className={`stats-card__icon ${iconColor}`}>{icon}</div>
        <div className="stats-card__title">{title}</div>
        <div className="stats-card__value">
          {typeof value === "number" ? value?.toLocaleString() : value || 0}
        </div>
        {period && <div className="stats-card__period">{period}</div>}
      </div>
    </div>
  );

  // Chart Card Component
  const ChartCard = ({ title, icon, children }) => (
    <div className="chart-card">
      <div className="chart-card__header">
        <h3 className="chart-card__title">
          {icon}
          {title}
        </h3>
      </div>
      {children}
    </div>
  );

  // Process chart data for overview
  const growthChartOptions = {
    chart: { type: "area", toolbar: { show: false }, zoom: { enabled: false } },
    colors: ["#8b5cf6", "#22c55e"],
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1, stops: [0, 100] },
    },
    stroke: { curve: "smooth", width: 2 },
    dataLabels: { enabled: false },
    xaxis: {
      categories: analytics?.growthTrend?.map((item) => item._id) || [],
      labels: { style: { colors: "#64748b" } },
    },
    yaxis: { labels: { style: { colors: "#64748b" } } },
    grid: { borderColor: "#f1f5f9" },
    tooltip: { theme: "light" },
    legend: { position: "top" },
  };

  const growthChartSeries = [
    { name: "New Users", data: analytics?.growthTrend?.map((item) => item.count) || [] },
    { name: "Visitors", data: analytics?.visitorTrend?.map((item) => item.count) || [] },
  ];

  // Plan distribution pie chart
  const planChartOptions = {
    labels: analytics?.planDistribution?.map((item) => item._id || "Free") || [],
    colors: ["#8b5cf6", "#22c55e", "#3b82f6", "#f59e0b", "#ec4899"],
    legend: { position: "bottom" },
    dataLabels: { enabled: true },
  };
  const planChartSeries = analytics?.planDistribution?.map((item) => item.count) || [];

  // Top countries bar chart
  const countriesChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    colors: ["#8b5cf6"],
    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
    xaxis: {
      categories: analytics?.topVisitorCountries?.map((c) => c._id || "Unknown") || [],
      labels: { style: { colors: "#64748b" } },
    },
    grid: { borderColor: "#f1f5f9" },
    dataLabels: { enabled: false },
  };
  const countriesChartSeries = [
    { name: "Visitors", data: analytics?.topVisitorCountries?.map((c) => c.count) || [] },
  ];

  // Traffic sources chart
  const sourcesChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    colors: ["#22c55e"],
    plotOptions: { bar: { horizontal: false, borderRadius: 4, columnWidth: "50%" } },
    xaxis: {
      categories: analytics?.topVisitorSources?.map((s) => s._id || "Direct") || [],
      labels: { style: { colors: "#64748b" } },
    },
    grid: { borderColor: "#f1f5f9" },
    dataLabels: { enabled: false },
  };
  const sourcesChartSeries = [
    { name: "Visitors", data: analytics?.topVisitorSources?.map((s) => s.count) || [] },
  ];

  // Usage - Generations trend chart
  const generationsTrendOptions = {
    chart: { type: "area", toolbar: { show: false }, zoom: { enabled: false } },
    colors: ["#8b5cf6", "#f59e0b"],
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1, stops: [0, 100] },
    },
    stroke: { curve: "smooth", width: 2 },
    dataLabels: { enabled: false },
    xaxis: {
      categories: usageAnalytics?.generationsTrend?.map((item) => item._id) || [],
      labels: { style: { colors: "#64748b" } },
    },
    yaxis: { labels: { style: { colors: "#64748b" } } },
    grid: { borderColor: "#f1f5f9" },
    tooltip: { theme: "light" },
    legend: { position: "top" },
  };
  const generationsTrendSeries = [
    { name: "Generations", data: usageAnalytics?.generationsTrend?.map((item) => item.count) || [] },
    { name: "Tokens (K)", data: usageAnalytics?.generationsTrend?.map((item) => Math.round(item.tokens / 1000)) || [] },
  ];

  // Category usage chart
  const categoryChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    colors: ["#ec4899"],
    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
    xaxis: {
      categories: usageAnalytics?.categoryUsage?.map((c) => c.name || "Unknown") || [],
      labels: { style: { colors: "#64748b" } },
    },
    grid: { borderColor: "#f1f5f9" },
    dataLabels: { enabled: false },
  };
  const categoryChartSeries = [
    { name: "Generations", data: usageAnalytics?.categoryUsage?.map((c) => c.count) || [] },
  ];

  // Retention - New users trend chart
  const newUsersTrendOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    colors: ["#22c55e"],
    plotOptions: { bar: { borderRadius: 4, columnWidth: "60%" } },
    xaxis: {
      categories: retentionAnalytics?.newUsersTrend?.map((item) => item._id?.slice(5)) || [],
      labels: { style: { colors: "#64748b" } },
    },
    yaxis: { labels: { style: { colors: "#64748b" } } },
    grid: { borderColor: "#f1f5f9" },
    dataLabels: { enabled: false },
  };
  const newUsersTrendSeries = [
    { name: "New Users", data: retentionAnalytics?.newUsersTrend?.map((item) => item.count) || [] },
  ];

  // Streak distribution chart
  const streakChartOptions = {
    labels: retentionAnalytics?.streakDistribution?.map((s) => `${s.label} days`) || [],
    colors: ["#8b5cf6", "#22c55e", "#3b82f6", "#f59e0b", "#ec4899", "#14b8a6", "#ef4444"],
    legend: { position: "bottom" },
    dataLabels: { enabled: true },
  };
  const streakChartSeries = retentionAnalytics?.streakDistribution?.map((s) => s.count) || [];

  // Account types chart
  const accountTypesOptions = {
    labels: retentionAnalytics?.accountTypes?.map((a) => a._id || "Local") || [],
    colors: ["#8b5cf6", "#3b82f6"],
    legend: { position: "bottom" },
  };
  const accountTypesSeries = retentionAnalytics?.accountTypes?.map((a) => a.count) || [];

  // Device icon helper
  const getDeviceIcon = (device) => {
    switch (device?.toLowerCase()) {
      case "mobile":
        return <Smartphone size={16} />;
      case "tablet":
        return <Tablet size={16} />;
      default:
        return <Monitor size={16} />;
    }
  };

  // Tab content renderer
  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Users size={24} color="#8b5cf6" />} iconColor="purple" title="Total Users" value={analytics?.totalAdmins} period="All time" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<UserPlus size={24} color="#22c55e" />} iconColor="green" title="New Users Today" value={analytics?.totalNewAdminsToday} period="Today" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Eye size={24} color="#3b82f6" />} iconColor="blue" title="Total Visitors" value={analytics?.totalVisitors} period="All time" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Globe size={24} color="#f59e0b" />} iconColor="orange" title="Countries" value={analytics?.totalVisitorCountries} period="Unique countries" />
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<TrendingUp size={24} color="#ec4899" />} iconColor="pink" title="New Users (7 Days)" value={analytics?.totalNewAdminsLast7Days} period="Last 7 days" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Activity size={24} color="#14b8a6" />} iconColor="teal" title="Active Users" value={analytics?.activeUsersLast7Days} period="Last 7 days" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Users size={24} color="#8b5cf6" />} iconColor="purple" title="Teams" value={analytics?.usersWithTeam} period="Users with team" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<TrendingUp size={24} color="#22c55e" />} iconColor="green" title="Conversion Rate" value={`${analytics?.conversionRate || 0}%`} period="Visitors to signups" />
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={24}>
                <ChartCard title="Growth Trend" icon={<TrendingUp size={18} color="#8b5cf6" style={{ marginRight: 8 }} />}>
                  <Chart options={growthChartOptions} series={growthChartSeries} type="area" height={350} />
                </ChartCard>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <ChartCard title="Plan Distribution" icon={<PieChart size={18} color="#8b5cf6" style={{ marginRight: 8 }} />}>
                  {planChartSeries.length > 0 ? (
                    <Chart options={planChartOptions} series={planChartSeries} type="donut" height={300} />
                  ) : (
                    <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>No data</div>
                  )}
                </ChartCard>
              </Col>
              <Col xs={24} lg={12}>
                <ChartCard title="Top Countries" icon={<Globe size={18} color="#8b5cf6" style={{ marginRight: 8 }} />}>
                  <Chart options={countriesChartOptions} series={countriesChartSeries} type="bar" height={300} />
                </ChartCard>
              </Col>
            </Row>
          </>
        );

      case "usage":
        return (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<FileText size={24} color="#8b5cf6" />} iconColor="purple" title="Total Generations" value={usageAnalytics?.totalGenerations} period="All time" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Zap size={24} color="#f59e0b" />} iconColor="orange" title="Total Tokens" value={usageAnalytics?.totalTokens?.toLocaleString()} period="All time" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<MessageSquare size={24} color="#3b82f6" />} iconColor="blue" title="Total Chats" value={usageAnalytics?.totalChats} period="All time" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<BarChart3 size={24} color="#22c55e" />} iconColor="green" title="Avg Tokens/Gen" value={usageAnalytics?.avgTokensPerGeneration} period="Per generation" />
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<FileText size={24} color="#ec4899" />} iconColor="pink" title="Generations (Range)" value={usageAnalytics?.generationsInRange} period={`Last ${timeRange}`} />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<ThumbsUp size={24} color="#22c55e" />} iconColor="green" title="Useful Generations" value={usageAnalytics?.usefulGenerations} period="Rated useful" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<ThumbsDown size={24} color="#ef4444" />} iconColor="red" title="Poor Generations" value={usageAnalytics?.poorGenerations} period="Rated poor" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<TrendingUp size={24} color="#8b5cf6" />} iconColor="purple" title="Quality Ratio" value={`${usageAnalytics?.qualityRatio || 0}%`} period="Useful rate" />
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Users size={24} color="#14b8a6" />} iconColor="teal" title="Custom Copilots" value={usageAnalytics?.customCopilotCount} period="Created by users" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<FileText size={24} color="#3b82f6" />} iconColor="blue" title="Custom Generations" value={usageAnalytics?.customCopilotGenerations} period="From custom copilots" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Zap size={24} color="#f59e0b" />} iconColor="orange" title="Craft Generations" value={usageAnalytics?.craftGenerations} period="Using craft feature" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<MessageSquare size={24} color="#ec4899" />} iconColor="pink" title="Total Messages" value={usageAnalytics?.totalMessages} period="Chat messages" />
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={24}>
                <ChartCard title="Generations Trend" icon={<TrendingUp size={18} color="#8b5cf6" style={{ marginRight: 8 }} />}>
                  <Chart options={generationsTrendOptions} series={generationsTrendSeries} type="area" height={350} />
                </ChartCard>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <ChartCard title="Popular Categories" icon={<BarChart3 size={18} color="#ec4899" style={{ marginRight: 8 }} />}>
                  {usageAnalytics?.categoryUsage?.length > 0 ? (
                    <Chart options={categoryChartOptions} series={categoryChartSeries} type="bar" height={300} />
                  ) : (
                    <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>No category data</div>
                  )}
                </ChartCard>
              </Col>
              <Col xs={24} lg={12}>
                <ChartCard title="Popular Copilots" icon={<Users size={18} color="#8b5cf6" style={{ marginRight: 8 }} />}>
                  <div style={{ padding: "10px 0" }}>
                    {usageAnalytics?.popularCopilots?.length > 0 ? (
                      usageAnalytics.popularCopilots.map((copilot, index) => (
                        <div key={index} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderRadius: 8, marginBottom: 8, background: "#faf5ff" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <span style={{ fontWeight: 600, color: "#8b5cf6", width: 24 }}>#{index + 1}</span>
                            <span style={{ fontWeight: 500, color: "#1e293b" }}>{copilot.name}</span>
                          </div>
                          <span style={{ fontWeight: 600, color: "#8b5cf6" }}>{copilot.used?.toLocaleString()} uses</span>
                        </div>
                      ))
                    ) : (
                      <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>No copilot data</div>
                    )}
                  </div>
                </ChartCard>
              </Col>
            </Row>
          </>
        );

      case "retention":
        return (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Users size={24} color="#8b5cf6" />} iconColor="purple" title="Total Users" value={retentionAnalytics?.totalUsers} period="All time" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Activity size={24} color="#22c55e" />} iconColor="green" title="DAU" value={retentionAnalytics?.dailyActiveUsers} period={`${retentionAnalytics?.dauRate || 0}% of users`} />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Activity size={24} color="#3b82f6" />} iconColor="blue" title="WAU" value={retentionAnalytics?.weeklyActiveUsers} period={`${retentionAnalytics?.wauRate || 0}% of users`} />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Activity size={24} color="#f59e0b" />} iconColor="orange" title="MAU" value={retentionAnalytics?.monthlyActiveUsers} period={`${retentionAnalytics?.mauRate || 0}% of users`} />
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<UserCheck size={24} color="#22c55e" />} iconColor="green" title="Onboarding Done" value={retentionAnalytics?.onboardingComplete} period={`${retentionAnalytics?.onboardingRate || 0}% completion`} />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Mail size={24} color="#3b82f6" />} iconColor="blue" title="Email Verified" value={retentionAnalytics?.emailVerified} period={`${retentionAnalytics?.emailVerificationRate || 0}% verified`} />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Flame size={24} color="#f59e0b" />} iconColor="orange" title="Users with Streaks" value={retentionAnalytics?.usersWithStreaks} period="Active streakers" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<TrendingUp size={24} color="#ec4899" />} iconColor="pink" title="Avg Login Streak" value={retentionAnalytics?.avgLoginStreak} period="Days average" />
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Share2 size={24} color="#8b5cf6" />} iconColor="purple" title="Referred Users" value={retentionAnalytics?.referredUsers} period="Joined via referral" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Users size={24} color="#14b8a6" />} iconColor="teal" title="Has Referral Code" value={retentionAnalytics?.usersWithReferralCode} period="Can refer others" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Smartphone size={24} color="#ec4899" />} iconColor="pink" title="Mobile Onboarded" value={retentionAnalytics?.mobileOnboardingComplete} period="Mobile app users" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Activity size={24} color="#22c55e" />} iconColor="green" title="Online Now" value={retentionAnalytics?.usersOnline} period="Currently active" />
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={24}>
                <ChartCard title="New User Signups (30 Days)" icon={<TrendingUp size={18} color="#22c55e" style={{ marginRight: 8 }} />}>
                  <Chart options={newUsersTrendOptions} series={newUsersTrendSeries} type="bar" height={300} />
                </ChartCard>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <ChartCard title="Login Streak Distribution" icon={<Flame size={18} color="#f59e0b" style={{ marginRight: 8 }} />}>
                  {streakChartSeries.length > 0 ? (
                    <Chart options={streakChartOptions} series={streakChartSeries} type="pie" height={300} />
                  ) : (
                    <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>No streak data</div>
                  )}
                </ChartCard>
              </Col>
              <Col xs={24} lg={12}>
                <ChartCard title="Top Streak Users" icon={<Flame size={18} color="#ec4899" style={{ marginRight: 8 }} />}>
                  <div style={{ padding: "10px 0" }}>
                    {retentionAnalytics?.topStreakUsers?.length > 0 ? (
                      retentionAnalytics.topStreakUsers.slice(0, 5).map((user, index) => (
                        <div key={index} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderRadius: 8, marginBottom: 8, background: index < 3 ? "#fef3c7" : "#faf5ff" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <span style={{ fontWeight: 600, color: index < 3 ? "#d97706" : "#8b5cf6", width: 24 }}>#{index + 1}</span>
                            <div>
                              <div style={{ fontWeight: 500, color: "#1e293b" }}>{user.name}</div>
                              <div style={{ fontSize: 12, color: "#64748b" }}>{user.email}</div>
                            </div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontWeight: 600, color: "#f59e0b" }}>{user.longestLoginStreak} days</div>
                            <div style={{ fontSize: 12, color: "#64748b" }}>Current: {user.loginStreak}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>No streak data</div>
                    )}
                  </div>
                </ChartCard>
              </Col>
            </Row>
          </>
        );

      case "engagement":
        return (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Eye size={24} color="#3b82f6" />} iconColor="blue" title="Visitors Today" value={analytics?.totalVisitorsToday} period="Today" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Eye size={24} color="#8b5cf6" />} iconColor="purple" title="Visitors (7 Days)" value={analytics?.totalVisitorsLast7Days} period="Last 7 days" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Eye size={24} color="#22c55e" />} iconColor="green" title="Visitors (14 Days)" value={analytics?.totalVisitorsLast14Days} period="Last 14 days" />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard icon={<Eye size={24} color="#f59e0b" />} iconColor="orange" title="Visitors (30 Days)" value={analytics?.totalVisitorsLast30Days} period="Last 30 days" />
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={24}>
                <ChartCard title="Traffic Sources" icon={<BarChart3 size={18} color="#22c55e" style={{ marginRight: 8 }} />}>
                  <Chart options={sourcesChartOptions} series={sourcesChartSeries} type="bar" height={350} />
                </ChartCard>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <ChartCard title="Device Distribution" icon={<Monitor size={18} color="#8b5cf6" style={{ marginRight: 8 }} />}>
                  <div style={{ padding: "20px 0" }}>
                    {analytics?.deviceDistribution?.map((device, index) => (
                      <div key={index} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderRadius: 8, marginBottom: 8, background: "#faf5ff" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          {getDeviceIcon(device._id)}
                          <span style={{ fontWeight: 500, color: "#1e293b" }}>{device._id || "Desktop"}</span>
                        </div>
                        <span style={{ fontWeight: 600, color: "#8b5cf6" }}>{device.count?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </ChartCard>
              </Col>
              <Col xs={24} lg={12}>
                <ChartCard title="Top Browsers" icon={<Globe size={18} color="#8b5cf6" style={{ marginRight: 8 }} />}>
                  <div style={{ padding: "20px 0" }}>
                    {analytics?.browserDistribution?.map((browser, index) => (
                      <div key={index} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderRadius: 8, marginBottom: 8, background: "#f0fdf4" }}>
                        <span style={{ fontWeight: 500, color: "#1e293b" }}>{browser._id || "Unknown"}</span>
                        <span style={{ fontWeight: 600, color: "#22c55e" }}>{browser.count?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </ChartCard>
              </Col>
            </Row>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="super-admin-dashboard" style={{ overflowY: "auto", height: "100vh" }}>
      <div className="dashboard-header">
        <h1>
          Welcome back, {user?.name?.split(" ")[0] || "Admin"}
          <span>Here's what's happening with your platform</span>
        </h1>
        <Select value={timeRange} onChange={setTimeRange} className="time-range-select" style={{ width: 150 }}>
          <Option value="24h">Last 24 Hours</Option>
          <Option value="7d">Last 7 Days</Option>
          <Option value="30d">Last 30 Days</Option>
          <Option value="90d">Last 90 Days</Option>
        </Select>
      </div>

      <div className="dashboard-tabs">
        <button className={`dashboard-tab ${activeTab === "overview" ? "active" : ""}`} onClick={() => setActiveTab("overview")}>
          <BarChart3 size={16} />
          Overview
        </button>
        <button className={`dashboard-tab ${activeTab === "usage" ? "active" : ""}`} onClick={() => setActiveTab("usage")}>
          <Zap size={16} />
          Usage & AI
        </button>
        <button className={`dashboard-tab ${activeTab === "retention" ? "active" : ""}`} onClick={() => setActiveTab("retention")}>
          <Flame size={16} />
          Retention
        </button>
        <button className={`dashboard-tab ${activeTab === "engagement" ? "active" : ""}`} onClick={() => setActiveTab("engagement")}>
          <Activity size={16} />
          Engagement
        </button>
      </div>

      {loading ? (
        <div className="dashboard-loading">
          <Spin size="large" />
          <span>Loading analytics...</span>
        </div>
      ) : (
        renderTabContent()
      )}
    </div>
  );
};

export default SuperAdminDashboard;
